import { DatePipe, PercentPipe } from '@angular/common';
import { Component, computed, inject, input, signal, WritableSignal } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatTableModule,
} from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { PagedTableComponent } from 'common/table/paged-table.component';
import { MarkdownComponent } from 'ngx-markdown';
import { TableDataSource } from 'common/table/table-data-source';
import { ApiFinding, ApiV1FindingService, ApiV1Service, FindingType } from 'api';
import { DependencyTrackVulnerabilityFindingData } from 'component/details/requirement-intake/requirements-detail/findings/list/findings-list.model';
import {
  DependencyTrackFindingSummary,
  getDependencyTrackVulnerabilityData,
  prettyPrintJson,
} from 'component/details/requirement-intake/requirements-detail/findings/list/findings-list.utils';
import { FindingStateComponent } from 'component/details/finding-state/finding-state.component';
import { SeverityTagComponent } from 'common/severity-tag/severity-tag.component';
import { firstValueFrom } from 'rxjs';
import { FindingsFilteringSupport } from 'component/details/requirement-intake/requirements-detail/findings/list/findings-filtering-support';
import { MatDialog } from '@angular/material/dialog';
import {
  EvidenceFindingDialogComponent,
  FindingDescriptionDialogData,
  FindingDetailsDialogResult,
} from 'component/details/requirement-intake/requirements-detail/findings/details/evidence-finding-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from 'common/notification/notification.service';
import { FindingNameComponent } from 'component/details/requirement-intake/requirements-detail/findings/name/finding-name.component';
import { CWEsComponent } from 'component/details/requirement-intake/requirements-detail/findings/cwes/cwes.component';
import { IconDirective } from 'common/icon/icon.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-findings-list',
  templateUrl: 'findings-list.component.html',
  styleUrls: ['findings-list.component.scss'],
  imports: [
    TranslateModule,
    MatSortModule,
    MatTableModule,
    PagedTableComponent,
    MatColumnDef,
    MatHeaderCell,
    MatCellDef,
    MatHeaderCellDef,
    MatCell,
    DatePipe,
    MarkdownComponent,
    PercentPipe,
    FindingStateComponent,
    SeverityTagComponent,
    FindingNameComponent,
    CWEsComponent,
    IconDirective,
    ButtonDirective,
  ],
})
export class FindingsListComponent {
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiV1Service);
  private readonly findingService = inject(ApiV1FindingService);
  private readonly notificationService = inject(NotificationService);

  dataSource = input.required<TableDataSource<ApiFinding>>();
  findingsType = input<'default' | 'dtrack'>('default');
  protected readonly filteringSupport = computed(
    () => new FindingsFilteringSupport(this.findingsType()),
  );
  protected readonly expandedDescriptionIds = signal<ReadonlySet<number>>(new Set<number>());
  protected readonly expandedDataIds = signal<ReadonlySet<number>>(new Set<number>());
  private readonly dependencyTrackDataByFinding = new WeakMap<
    DependencyTrackFindingSummary,
    DependencyTrackVulnerabilityFindingData | undefined
  >();

  protected isDescriptionExpanded(findingId: number): boolean {
    return this.expandedDescriptionIds().has(findingId);
  }

  protected isDataExpanded(findingId: number): boolean {
    return this.expandedDataIds().has(findingId);
  }

  protected toggleData(findingId: number): void {
    this.toggleExpansion(this.expandedDataIds, findingId);
  }

  protected prettyPrintJson(data?: string | null): string {
    return prettyPrintJson(data);
  }

  protected getDependencyTrackVulnerabilityData(finding: {
    type?: string | null;
    data?: string | null;
  }): DependencyTrackVulnerabilityFindingData | undefined {
    if (this.dependencyTrackDataByFinding.has(finding)) {
      return this.dependencyTrackDataByFinding.get(finding);
    }

    const dependencyTrackData = getDependencyTrackVulnerabilityData(finding);
    this.dependencyTrackDataByFinding.set(finding, dependencyTrackData);
    return dependencyTrackData;
  }

  protected getCweIdsOnly(cweText?: string | null): string {
    if (!cweText) {
      return '';
    }

    const cweIds = cweText.match(/CWE-\d+/g);

    if (!cweIds || cweIds.length === 0) {
      return cweText;
    }

    return cweIds.join(', ');
  }

  protected toggleExpansion(expandedIds: WritableSignal<ReadonlySet<number>>, findingId: number) {
    expandedIds.update((expandedDataIds) => {
      const nextExpandedCWEIds = new Set(expandedDataIds);

      if (nextExpandedCWEIds.has(findingId)) {
        nextExpandedCWEIds.delete(findingId);
      } else {
        nextExpandedCWEIds.add(findingId);
      }

      return nextExpandedCWEIds;
    });
  }

  protected columnsToShow = computed(() => {
    if (this.findingsType() === 'dtrack') {
      return [
        'name',
        'severity',
        'dependencyTrackKEV',
        'dependencyTrackComponentName',
        'dependencyTrackComponentVersion',
        'dependencyTrackCvssV3BaseScore',
        'description',
        'status',
        'dependencyTrackPublished',
        'updatedAt',
      ];
    }
    return ['name', 'severity', 'description', 'status', 'filename', 'updatedAt'];
  });

  protected async onFindingStatusChange(
    finding: ApiFinding,
    updatedFinding: ApiFinding,
  ): Promise<void> {
    Object.assign(finding, updatedFinding);

    await firstValueFrom(
      this.findingService.patchFindingStatus(finding.id, {
        status: finding.status,
        statusJustification: finding.statusJustification,
      }),
    );
  }

  protected async openDescriptionDialog(finding: ApiFinding): Promise<void> {
    const evidenceName = await this.resolveEvidenceName(finding);

    const detailsDialogRef = this.dialog.open<
      EvidenceFindingDialogComponent,
      FindingDescriptionDialogData,
      FindingDetailsDialogResult
    >(EvidenceFindingDialogComponent, {
      data: {
        evidenceName: evidenceName,
        finding: finding,
        isAIgenerated: this.isAIgeneratedType(finding.type),
      },
    });
    const result = await firstValueFrom(detailsDialogRef.afterClosed());
    if (result === 'DELETE') {
      try {
        await firstValueFrom(this.findingService.deleteFindings([finding.id]));
        this.dataSource().refreshFromRoute(this.route);
      } catch (e) {
        this.notificationService.showHttpError(e, 'requirementIntake.details.findings.deleteError');
      }
    } else if (result?.properties?.name) {
      try {
        if (finding.type === FindingType.Manual) {
          await firstValueFrom(
            this.findingService.patchFindingProperties(finding.id, result.properties),
          );
        }
        if (
          result.properties.status !== finding.status ||
          result.statusJustification !== finding.statusJustification
        ) {
          await firstValueFrom(
            this.findingService.patchFindingStatus(finding.id, {
              status: result.properties.status,
              statusJustification: result.statusJustification,
            }),
          );
        }

        this.dataSource().refreshFromRoute(this.route);
      } catch (e) {
        this.notificationService.showHttpError(e, 'requirementIntake.details.findings.updateError');
      }
    }
  }

  private isAIgeneratedType(findingType: string): boolean {
    return !(
      findingType === 'DEPENDENCY_TRACK_VULNERABILITY' ||
      findingType === 'SBOM_FORMAT' ||
      findingType === 'NO_DEEP_SBOM' ||
      findingType === 'SBOM_MISSING_IDENTIFIER' ||
      findingType === 'MANUAL' ||
      findingType === 'OTHER'
    );
  }

  private async resolveEvidenceName(finding: ApiFinding): Promise<string | null> {
    if (!finding.evidenceId) {
      return null;
    }

    return (await firstValueFrom(this.apiService.getEvidence(finding.evidenceId))).name;
  }
}
